import React from "react";
import { useQuery } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Link as RouterLink, useParams } from "react-router-dom";
import BackButton from "../../_components/BackButton";
import {
  GET_HOMEOWNER_CLIENT,
  GET_ORGANISATION_CLIENT_PROFILE,
  LIST_HOMEOWNER_CLIENTS,
  LIST_ORGANISATION_CLIENT_PROFILES,
} from "./graphql";

type PageInfo = {
  hasNextPage: boolean;
  endCursor?: string | null;
};

type RelayResponse<T> = {
  pageInfo: PageInfo;
  edges: { cursor: string; node: T }[];
};

type ClientProfile = {
  id: string;
  ownerType: "HOMEOWNER" | "ORGANIZATION";
  createdAt?: string;
  updatedAt?: string;
};

type Address = {
  id: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  formattedAddress?: string | null;
};

type Homeowner = {
  id: string;
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  createdAt: string;
  updatedAt?: string;
  clientProfile?: ClientProfile | null;
  billingAddress?: Address | null;
  addresses?: Address[] | null;
};

type Organization = {
  id: string;
  name: string;
  referralCode?: string | null;
  countryCode?: string | null;
  createdAt?: string;
};

type LegacyClient = {
  id: string;
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
};

type OrganisationClientProfile = {
  id: string;
  contactUserId?: string;
  contactName: string;
  contactEmail: string;
  contactPhoneNumber: string;
  billingName: string;
  createdAt: string;
  updatedAt?: string;
  organization: Organization;
  clientProfile: ClientProfile;
  legacyClient?: LegacyClient | null;
  billingAddress?: Address | null;
};

const PAGE_SIZE = 20;

const shortId = (value?: string | null) => value?.slice(0, 8) || "N/A";

const formatDate = (value?: string | null) => {
  if (!value) return "N/A";

  return new Date(value).toLocaleString();
};

const formatName = (firstName?: string | null, lastName?: string | null) => {
  const fullName = `${firstName || ""} ${lastName || ""}`.trim();
  return fullName || "N/A";
};

const formatAddress = (address?: Address | null) => {
  if (!address) return "N/A";

  return (
    address.formattedAddress ||
    [address.address, address.city, address.state].filter(Boolean).join(", ") ||
    "N/A"
  );
};

const PageHeader = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <Flex
    align={{ base: "flex-start", md: "center" }}
    direction={{ base: "column", md: "row" }}
    justify="space-between"
    gap={4}
    mb={6}
  >
    <Box>
      <Heading as="h1" size="lg" color="primary.500" letterSpacing={0}>
        {title}
      </Heading>
      <Text color="primary.300" mt={1} mb={0}>
        {description}
      </Text>
    </Box>
  </Flex>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box bg="white" border="1px solid" borderColor="primary.150" borderRadius="md" p={5}>
    <Heading as="h2" size="sm" color="primary.500" letterSpacing={0} mb={4}>
      {title}
    </Heading>
    <Stack spacing={3}>{children}</Stack>
  </Box>
);

const FieldRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <Flex gap={4} justify="space-between" align="flex-start">
    <Text color="primary.300" fontSize="sm" minW="120px" mb={0}>
      {label}
    </Text>
    <Text color="primary.500" fontWeight="semibold" textAlign="right" mb={0}>
      {value || "N/A"}
    </Text>
  </Flex>
);

const LoadingState = () => (
  <Flex minH="180px" align="center" justify="center">
    <Spinner color="brand.500" />
  </Flex>
);

const ErrorState = ({ message = "Unable to load client profile data." }) => (
  <Alert status="error" borderRadius="md">
    <AlertIcon />
    <AlertDescription>{message}</AlertDescription>
  </Alert>
);

const EmptyState = ({ message }: { message: string }) => (
  <Flex minH="160px" align="center" justify="center" bg="primary.100" borderRadius="md">
    <Text color="primary.300" fontWeight="semibold" mb={0}>
      {message}
    </Text>
  </Flex>
);

const LoadMoreButton = ({
  loading,
  hasMore,
  onClick,
}: {
  loading: boolean;
  hasMore?: boolean;
  onClick: () => void;
}) => (
  <Flex justify="center" mt={4}>
    <Button
      variant="outline"
      colorScheme="brand"
      isLoading={loading}
      isDisabled={!hasMore}
      onClick={onClick}
    >
      {hasMore ? "Load more" : "All loaded"}
    </Button>
  </Flex>
);

export const HomeownerClientsList = () => {
  const { data, loading, error, fetchMore } = useQuery<{
    listHomeowners: RelayResponse<Homeowner>;
  }>(LIST_HOMEOWNER_CLIENTS, {
    variables: { first: PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
  });

  const homeowners = data?.listHomeowners.edges.map((edge) => edge.node) || [];

  if (error) return <ErrorState message={error.message} />;

  return (
    <Box>
      <PageHeader
        title="Homeowners"
        description="Homeowner client profiles registered on LaborHack."
      />
      {loading && !data ? (
        <LoadingState />
      ) : homeowners.length === 0 ? (
        <EmptyState message="No homeowner client profiles found." />
      ) : (
        <>
          <TableContainer bg="white" border="1px solid" borderColor="primary.150" borderRadius="md">
            <Table variant="simple">
              <Thead bg="primary.100">
                <Tr>
                  <Th>ID</Th>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Phone Number</Th>
                  <Th>Client Profile</Th>
                  <Th>Date Created</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {homeowners.map((homeowner) => (
                  <Tr key={homeowner.id}>
                    <Td>{shortId(homeowner.id)}</Td>
                    <Td fontWeight="semibold">
                      {formatName(homeowner.firstName, homeowner.lastName)}
                    </Td>
                    <Td>{homeowner.email || "N/A"}</Td>
                    <Td>{homeowner.phoneNumber || "N/A"}</Td>
                    <Td>{shortId(homeowner.clientProfile?.id)}</Td>
                    <Td>{formatDate(homeowner.createdAt)}</Td>
                    <Td>
                      <Button
                        as={RouterLink}
                        to={`/clients/homeowners/${homeowner.id}`}
                        size="sm"
                        variant="outline"
                        aria-label="View homeowner"
                      >
                        View
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          <LoadMoreButton
            loading={loading}
            hasMore={data?.listHomeowners.pageInfo.hasNextPage}
            onClick={() =>
              fetchMore({
                variables: {
                  first: PAGE_SIZE,
                  after: data?.listHomeowners.pageInfo.endCursor,
                },
              })
            }
          />
        </>
      )}
    </Box>
  );
};

export const HomeownerClientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery<{ homeowner: Homeowner }>(
    GET_HOMEOWNER_CLIENT,
    { variables: { id } }
  );

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} />;
  if (!data?.homeowner) return <EmptyState message="Homeowner client profile not found." />;

  const homeowner = data.homeowner;
  const clientProfile = homeowner.clientProfile;

  return (
    <Stack spacing={5}>
      <BackButton />
      <Box>
        <Heading as="h1" size="lg" color="primary.500" letterSpacing={0}>
          {formatName(homeowner.firstName, homeowner.lastName)}
        </Heading>
        <Flex gap={2} align="center" mt={2}>
          <Badge colorScheme="green">Homeowner</Badge>
          <Text color="primary.300" mb={0}>
            {homeowner.id}
          </Text>
        </Flex>
      </Box>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
        <Section title="Profile Summary">
          <FieldRow label="Homeowner ID" value={homeowner.id} />
          <FieldRow label="User ID" value={homeowner.userId} />
          <FieldRow label="Created" value={formatDate(homeowner.createdAt)} />
          <FieldRow label="Updated" value={formatDate(homeowner.updatedAt)} />
        </Section>
        <Section title="Personal Information">
          <FieldRow label="Name" value={formatName(homeowner.firstName, homeowner.lastName)} />
          <FieldRow label="Email" value={homeowner.email || "N/A"} />
          <FieldRow label="Phone" value={homeowner.phoneNumber || "N/A"} />
        </Section>
        <Section title="Client Profile">
          <FieldRow label="Profile ID" value={clientProfile?.id || "N/A"} />
          <FieldRow label="Owner Type" value={clientProfile?.ownerType || "N/A"} />
          <FieldRow label="Created" value={formatDate(clientProfile?.createdAt)} />
          <FieldRow label="Updated" value={formatDate(clientProfile?.updatedAt)} />
        </Section>
        <Section title="Billing And Addresses">
          <FieldRow label="Billing Address" value={formatAddress(homeowner.billingAddress)} />
          <Divider />
          <FieldRow label="Saved Addresses" value={homeowner.addresses?.length || 0} />
        </Section>
      </SimpleGrid>
    </Stack>
  );
};

export const OrganisationClientsList = () => {
  const { data, loading, error, fetchMore } = useQuery<{
    organizationClientProfiles: RelayResponse<OrganisationClientProfile>;
  }>(LIST_ORGANISATION_CLIENT_PROFILES, {
    variables: { first: PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
  });

  const profiles = data?.organizationClientProfiles.edges.map((edge) => edge.node) || [];

  if (error) return <ErrorState message={error.message} />;

  return (
    <Box>
      <PageHeader
        title="Organisations"
        description="Organisation client profiles that can request services."
      />
      {loading && !data ? (
        <LoadingState />
      ) : profiles.length === 0 ? (
        <EmptyState message="No organisation client profiles found." />
      ) : (
        <>
          <TableContainer bg="white" border="1px solid" borderColor="primary.150" borderRadius="md">
            <Table variant="simple">
              <Thead bg="primary.100">
                <Tr>
                  <Th>Organisation</Th>
                  <Th>Organisation ID</Th>
                  <Th>Contact</Th>
                  <Th>Email</Th>
                  <Th>Phone</Th>
                  <Th>Billing Name</Th>
                  <Th>Client Profile</Th>
                  <Th>Date Created</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {profiles.map((profile) => (
                  <Tr key={profile.id}>
                    <Td fontWeight="semibold">{profile.organization.name}</Td>
                    <Td>{shortId(profile.organization.id)}</Td>
                    <Td>{profile.contactName}</Td>
                    <Td>{profile.contactEmail}</Td>
                    <Td>{profile.contactPhoneNumber}</Td>
                    <Td>{profile.billingName}</Td>
                    <Td>{shortId(profile.clientProfile.id)}</Td>
                    <Td>{formatDate(profile.createdAt)}</Td>
                    <Td>
                      <Button
                        as={RouterLink}
                        to={`/clients/organisations/${profile.id}`}
                        size="sm"
                        variant="outline"
                        aria-label="View organisation"
                      >
                        View
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          <LoadMoreButton
            loading={loading}
            hasMore={data?.organizationClientProfiles.pageInfo.hasNextPage}
            onClick={() =>
              fetchMore({
                variables: {
                  first: PAGE_SIZE,
                  after: data?.organizationClientProfiles.pageInfo.endCursor,
                },
              })
            }
          />
        </>
      )}
    </Box>
  );
};

export const OrganisationClientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useQuery<{
    organizationClientProfile: OrganisationClientProfile | null;
  }>(GET_ORGANISATION_CLIENT_PROFILE, { variables: { id } });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} />;
  if (!data?.organizationClientProfile) {
    return <EmptyState message="Organisation client profile not found." />;
  }

  const profile = data.organizationClientProfile;

  return (
    <Stack spacing={5}>
      <BackButton />
      <Box>
        <Heading as="h1" size="lg" color="primary.500" letterSpacing={0}>
          {profile.organization.name}
        </Heading>
        <Flex gap={2} align="center" mt={2}>
          <Badge colorScheme="blue">Organisation</Badge>
          <Text color="primary.300" mb={0}>
            {profile.id}
          </Text>
        </Flex>
      </Box>
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
        <Section title="Organisation Information">
          <FieldRow label="Organisation ID" value={profile.organization.id} />
          <FieldRow label="Name" value={profile.organization.name} />
          <FieldRow label="Referral Code" value={profile.organization.referralCode || "N/A"} />
          <FieldRow label="Country" value={profile.organization.countryCode || "N/A"} />
          <FieldRow label="Created" value={formatDate(profile.organization.createdAt)} />
        </Section>
        <Section title="Client Profile">
          <FieldRow label="Metadata ID" value={profile.id} />
          <FieldRow label="Profile ID" value={profile.clientProfile.id} />
          <FieldRow label="Owner Type" value={profile.clientProfile.ownerType} />
          <FieldRow label="Created" value={formatDate(profile.clientProfile.createdAt)} />
          <FieldRow label="Updated" value={formatDate(profile.clientProfile.updatedAt)} />
        </Section>
        <Section title="Contact Person">
          <FieldRow label="Name" value={profile.contactName} />
          <FieldRow label="Email" value={profile.contactEmail} />
          <FieldRow label="Phone" value={profile.contactPhoneNumber} />
          <FieldRow label="User ID" value={profile.contactUserId || "N/A"} />
        </Section>
        <Section title="Billing">
          <FieldRow label="Billing Name" value={profile.billingName} />
          <FieldRow label="Billing Address" value={formatAddress(profile.billingAddress)} />
        </Section>
        <Section title="Legacy Client Compatibility">
          <FieldRow label="Client ID" value={profile.legacyClient?.id || "N/A"} />
          <FieldRow label="User ID" value={profile.legacyClient?.userId || "N/A"} />
          <FieldRow
            label="Name"
            value={formatName(profile.legacyClient?.firstName, profile.legacyClient?.lastName)}
          />
          <FieldRow label="Email" value={profile.legacyClient?.email || "N/A"} />
          <FieldRow label="Phone" value={profile.legacyClient?.phoneNumber || "N/A"} />
        </Section>
      </SimpleGrid>
    </Stack>
  );
};
